import { Injectable } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, firstValueFrom, from, Observable, of, switchMap } from 'rxjs';

import { IocoinsReferralService } from '@core/services/iocoins-referral.service';
import { AuthTokenService } from '@core/services/auth-token.service';
import { GeneralService } from '@core/services/general.service';

@Injectable({ providedIn: 'root' })
export class AssociateGuard implements CanActivate {
  messageConfig = {
    header: 'Aviso',
    acceptLabel: 'Aceptar',
    rejectVisible: false,
    icon: 'pi pi-info-circle',
  };

  constructor(
    private readonly router: Router,
    private readonly authTokenService: AuthTokenService,
    private readonly generalService: GeneralService,
    private readonly iocoinsReferralService: IocoinsReferralService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return from(Promise.resolve()).pipe(
      switchMap(async () => {
        const refCodeParam = route.paramMap.get('refCode')?.trim();
        const refCode = refCodeParam?.length === 6 ? refCodeParam : null;

        if (!refCode) {
          const message = refCodeParam
            ? `El código de referido <b>${refCodeParam}</b> no es válido.`
            : `El código de referido no es válido.`;
          this.generalService.showConfirmation({ ...this.messageConfig, message });
          return this.router.createUrlTree(['/login']);
        }

        const { exists } = await firstValueFrom(
          this.iocoinsReferralService
            .refcodeExists(refCode)
            .pipe(catchError(() => of({ exists: false })))
        );

        if (!exists) {
          const message = `El código de referido <b>${refCodeParam}</b> no existe.`;
          this.generalService.showConfirmation({ ...this.messageConfig, message });
          return this.router.createUrlTree(['/login']);
        }

        const tokenIsExpired = this.authTokenService.isTokenExpired();

        if (!tokenIsExpired) {
          const [miReferidor, refcodeValidation] = await Promise.all([
            firstValueFrom(this.iocoinsReferralService.getMyReferralParent()),
            firstValueFrom(this.iocoinsReferralService.refcodeValidateFromUser(refCode)),
          ]);
          if (miReferidor) {
            const fullname = new TitleCasePipe().transform(
              `${miReferidor.firstname} ${miReferidor.lastname}`.trim()
            );
            const message = `Tu cuenta está asociada al referidor <b>${fullname}</b>. No es posible cambiar de referidor.`;
            this.generalService.showConfirmation({ ...this.messageConfig, message });
            return this.router.createUrlTree(['/mis-referidos'], {
              queryParams: { tab: 'referidores' },
            });
          }

          if (refcodeValidation && refcodeValidation.valid === false) {
            const message = refcodeValidation.message;
            this.generalService.showConfirmation({ ...this.messageConfig, message });
            return this.router.createUrlTree(['/mis-referidos'], {
              queryParams: { tab: 'referidores' },
            });
          }
        }

        return true;
      })
    );
  }
}
